import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  useListRecipeDrafts,
  getListRecipeDraftsQueryKey,
  useStartRecipeDrafts,
  useRefreshRecipeDraftBaseline,
  useApproveRecipeDrafts,
  type RecipeDraftRow,
  type RecipeDraftList,
  type RecipeDraftSkipReason,
} from "@workspace/api-client-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { AlertTriangle, CheckCircle2, ClipboardCheck, Loader2, Play, RotateCcw, Search, ShieldAlert, X } from "lucide-react";
import { RecipeDraftReviewDialog } from "@/components/recipe-draft-review-dialog";
import { errorMessage } from "@/components/cost-recipe-panel";
import {
  APPROVAL_BATCH_SIZE, STATUS_LABELS, approvalBlocker, asApprovalResult, chunk, formatGBP,
  formatSignedGBP, invalidateAfterPublish, invalidateDrafts, isBalanced,
} from "@/lib/recipe-drafts";

type StatusFilter = "all" | RecipeDraftRow["status"] | "failed" | "unbalanced" | "stale" | "ready";

const STATUS_TONE: Record<RecipeDraftRow["status"], string> = {
  not_drafted: "bg-muted text-muted-foreground",
  draft: "bg-sky-100 text-sky-900 dark:bg-sky-900/50 dark:text-sky-100",
  approved: "bg-emerald-100 text-emerald-900 dark:bg-emerald-900/50 dark:text-emerald-100",
  rejected: "bg-slate-200 text-slate-800 dark:bg-slate-700 dark:text-slate-100",
  redraft_requested: "bg-amber-100 text-amber-900 dark:bg-amber-900/50 dark:text-amber-100",
};

type BulkOutcome = { approved: number; skipped: number; details: RecipeDraftSkipReason[]; failedBatches: number; errors: string[] };

function matchesStatus(r: RecipeDraftRow, f: StatusFilter): boolean {
  switch (f) {
    case "all": return true;
    case "failed": return r.generationState === "failed";
    case "unbalanced": return !!r.draftId && !isBalanced(r) && r.status !== "approved";
    case "stale": return r.isStale;
    case "ready": return approvalBlocker(r) === null && !r.reviewWarnings?.length;
    default: return r.status === f;
  }
}

export default function AdminRecipeDraftsPage() {
  const { data: adminCheck, isLoading: adminLoading } = useQuery<{ isAdmin: boolean }>({ queryKey: ["/api/admin/check"], retry: false });
  if (adminLoading) return <div className="p-6"><Skeleton className="h-8 w-64" /></div>;
  if (adminCheck?.isAdmin !== true) {
    return (
      <div className="p-6 max-w-lg mx-auto text-center space-y-2" data-testid="text-admin-required">
        <ShieldAlert className="h-8 w-8 mx-auto text-muted-foreground" />
        <h1 className="text-lg font-semibold">Admin only</h1>
        <p className="text-sm text-muted-foreground">Recipe draft review is limited to builder admins.</p>
      </div>
    );
  }
  return <RecipeDraftsWorkspace />;
}

function RecipeDraftsWorkspace() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [category, setCategory] = useState("all");
  const [status, setStatus] = useState<StatusFilter>("all");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [openRow, setOpenRow] = useState<RecipeDraftRow | null>(null);
  const [confirmBulk, setConfirmBulk] = useState(false);
  const [confirmBaseline, setConfirmBaseline] = useState(false);
  const [bulkRunning, setBulkRunning] = useState<{ done: number; total: number } | null>(null);
  const [outcome, setOutcome] = useState<BulkOutcome | null>(null);

  const listQ = useListRecipeDrafts({
    query: {
      queryKey: getListRecipeDraftsQueryKey(),
      refetchInterval: (q) => {
        const d = q.state.data as RecipeDraftList | undefined;
        if (!d) return false;
        return d.runActive || d.progress.queued + d.progress.running > 0 ? 4000 : 30000;
      },
      refetchOnWindowFocus: true,
    },
  });
  const data = listQ.data;
  const rows = useMemo(() => data?.rows ?? [], [data]);

  const start = useStartRecipeDrafts({
    mutation: {
      onSuccess: (r, vars) => {
        toast({ title: r.queued ? (vars.data.retryFailures ? "Retrying failed drafts" : "Drafting started") : "Nothing to queue", description: r.queued ? "Progress updates below. Nothing is approved automatically." : "Every eligible task already has a draft or a live recipe." });
        void invalidateDrafts(qc);
      },
      onError: (err) => toast({ title: "Could not start drafting", description: errorMessage(err), variant: "destructive" }),
    },
  });
  const baseline = useRefreshRecipeDraftBaseline({
    mutation: {
      onSuccess: () => {
        setConfirmBaseline(false);
        toast({ title: "Safety snapshot refreshed", description: "Stale drafts still need redrafting before approval." });
        void invalidateDrafts(qc);
      },
      onError: (err) => { setConfirmBaseline(false); toast({ title: "Could not refresh snapshot", description: errorMessage(err), variant: "destructive" }); },
    },
  });
  const approve = useApproveRecipeDrafts();

  const categories = useMemo(() => Array.from(new Set(rows.map((r) => r.category))).sort(), [rows]);
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => (category === "all" || r.category === category) && matchesStatus(r, status) && (!q || r.name.toLowerCase().includes(q)));
  }, [rows, category, status, search]);
  const failures = useMemo(() => rows.filter((r) => r.generationState === "failed"), [rows]);

  const selectedRows = useMemo(() => rows.filter((r) => selected.has(r.id)), [rows, selected]);
  const selectedBlocked = selectedRows.filter((r) => approvalBlocker(r) !== null).length;
  const batchCount = Math.ceil(selectedRows.length / APPROVAL_BATCH_SIZE);
  const allFilteredSelected = filtered.length > 0 && filtered.every((r) => selected.has(r.id));

  function toggle(id: string, on: boolean) {
    setSelected((prev) => { const n = new Set(prev); if (on) n.add(id); else n.delete(id); return n; });
  }
  function toggleAllFiltered(on: boolean) {
    setSelected((prev) => { const n = new Set(prev); for (const r of filtered) { if (on) n.add(r.id); else n.delete(r.id); } return n; });
  }

  async function runBulkApprove() {
    setConfirmBulk(false);
    const ids = selectedRows.map((r) => r.id);
    const batches = chunk(ids, APPROVAL_BATCH_SIZE);
    const acc: BulkOutcome = { approved: 0, skipped: 0, details: [], failedBatches: 0, errors: [] };
    setOutcome(null);
    setBulkRunning({ done: 0, total: batches.length });
    for (let i = 0; i < batches.length; i++) {
      try {
        const r = await approve.mutateAsync({ data: { taskIds: batches[i] } });
        acc.approved += r.approved; acc.skipped += r.skipped; acc.details.push(...r.details);
      } catch (err) {
        const r = asApprovalResult(err);
        if (r) { acc.approved += r.approved; acc.skipped += r.skipped; acc.details.push(...r.details); }
        else { acc.failedBatches += 1; acc.errors.push(`Batch ${i + 1}: ${errorMessage(err)}`); }
      }
      setBulkRunning({ done: i + 1, total: batches.length });
    }
    setBulkRunning(null);
    setOutcome(acc);
    setSelected(new Set());
    await invalidateAfterPublish(qc);
    toast({
      title: `${acc.approved} approved, ${acc.skipped} skipped`,
      description: acc.failedBatches ? `${acc.failedBatches} batch(es) failed - see the report.` : "Typicals unchanged. See the report for skipped rows.",
      variant: acc.failedBatches ? "destructive" : undefined,
    });
  }

  const nameById = useMemo(() => new Map(rows.map((r) => [r.id, r.name])), [rows]);
  const p = data?.progress;
  const safety = data?.safety;
  const safetyConflict = !!safety && (!safety.ratesMatch || !safety.quotesMatch);
  const queueActive = !!data && (data.runActive || data.progress.queued + data.progress.running > 0);
  const draftedPct = p && p.eligible > 0 ? Math.min(100, (p.drafted / p.eligible) * 100) : 0;

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-lg sm:text-2xl font-bold flex items-center gap-2"><ClipboardCheck className="h-5 w-5" /> Recipe Drafts</h1>
          <p className="text-muted-foreground text-sm max-w-2xl">
            AI-drafted breakdowns for UK default tasks, held in staging. Nothing is live until you approve it, and approval never changes a task's typical, low, high or lock.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => start.mutate({ data: {} })} disabled={start.isPending || queueActive || !data} data-testid="button-draft-all">
            {start.isPending || queueActive ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Play className="h-4 w-4 mr-1.5" />}
            {queueActive ? "Drafting..." : "Draft all UK tasks"}
          </Button>
          <Button variant="outline" onClick={() => start.mutate({ data: { retryFailures: true } })} disabled={start.isPending || queueActive || failures.length === 0} data-testid="button-retry-failures">
            <RotateCcw className="h-4 w-4 mr-1.5" /> Retry failures{failures.length ? ` (${failures.length})` : ""}
          </Button>
        </div>
      </div>

      {listQ.isLoading ? (
        <div className="grid gap-3 sm:grid-cols-[2fr_3fr]"><Skeleton className="h-28" /><Skeleton className="h-28" /></div>
      ) : listQ.isError || !data || !p ? (
        <Card><CardContent className="py-8 flex flex-col items-center gap-3 text-sm" data-testid="error-recipe-drafts">
          <span className="text-destructive">Could not load recipe drafts. {errorMessage(listQ.error)}</span>
          <Button variant="outline" size="sm" onClick={() => listQ.refetch()} data-testid="button-retry-list">Retry</Button>
        </CardContent></Card>
      ) : (
        <>
          <div className="grid gap-3 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
            <Card>
              <CardContent className="p-4 space-y-1">
                <div className="text-xs uppercase tracking-wide text-muted-foreground">Approved</div>
                <p className="text-base sm:text-lg font-semibold tabular-nums" data-testid="text-approval-counter">
                  {p.approved} of {p.total} approved · top 50: {p.top50Approved} of 50 approved
                </p>
                <p className="text-xs text-muted-foreground">Includes the three already-reviewed seeded recipes. Usage ranks come from development data, not production usage.</p>
                <p className="text-xs tabular-nums" data-testid="text-coverage-summary">
                  {rows.filter(r => approvalBlocker(r) === null && !r.reviewWarnings?.length).length} ready for owner review ·{" "}
                  {rows.filter(r => !r.hasLiveRecipe && r.draftId && (approvalBlocker(r) !== null || !!r.reviewWarnings?.length)).length} need attention ·{" "}
                  {rows.filter(r => !r.hasLiveRecipe && !r.draftId).length} not drafted
                </p>
                <p className="text-xs text-muted-foreground">Ready means checks passed, not independently verified prices. Review scope and assumptions before approving. Approval enables defaults in this environment; Update Live is a separate action.</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 space-y-2">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <div className="text-xs uppercase tracking-wide text-muted-foreground">Drafting progress</div>
                  <span className="text-sm font-semibold tabular-nums" data-testid="text-draft-progress">{p.drafted} of {p.eligible} drafted</span>
                </div>
                <Progress value={draftedPct} className="h-2" />
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground tabular-nums" data-testid="text-progress-detail">
                  <span>Queued {p.queued}</span><span>Running {p.running}</span>
                  <span className={p.failed ? "text-destructive font-medium" : ""}>Failed {p.failed}</span>
                  <span>Balanced first time {p.firstBalanced}</span><span>Labour-floor adjusted {p.labourFloorAdjusted}</span><span>Retries {p.retries}</span>
                </div>
              </CardContent>
            </Card>
          </div>

          {safety && (safetyConflict || safety.message) && (
            <div className={`rounded-md border p-3 text-sm flex flex-col sm:flex-row sm:items-center gap-3 ${safetyConflict ? "border-destructive/40 bg-destructive/5" : "bg-muted/50"}`} data-testid="banner-safety">
              <AlertTriangle className={`h-4 w-4 shrink-0 ${safetyConflict ? "text-destructive" : "text-muted-foreground"}`} />
              <div className="flex-1 min-w-0 space-y-0.5">
                <p className="font-medium">{safetyConflict ? "Safety snapshot no longer matches" : "Safety snapshot"}</p>
                <p className="text-xs text-muted-foreground">
                  {safety.message ?? ""}{" "}
                  {!safety.ratesMatch && "Task rates differ from the snapshot. "}{!safety.quotesMatch && "Saved quote totals differ from the snapshot. "}
                  {safety.capturedAt && `Captured ${new Date(safety.capturedAt).toLocaleString("en-GB")}.`}
                </p>
              </div>
              {safetyConflict && (
                <Button size="sm" variant="outline" onClick={() => setConfirmBaseline(true)} disabled={baseline.isPending} data-testid="button-refresh-baseline">
                  Refresh safety snapshot
                </Button>
              )}
            </div>
          )}

          {failures.length > 0 && (
            <details className="rounded-md border p-3 text-sm" data-testid="list-failures">
              <summary className="cursor-pointer font-medium text-destructive">{failures.length} drafting failure{failures.length === 1 ? "" : "s"}</summary>
              <ul className="mt-2 space-y-1 text-xs max-h-56 overflow-y-auto">
                {failures.map((f) => (
                  <li key={f.id} className="flex flex-col sm:flex-row gap-x-2"><button type="button" className="font-medium text-left underline-offset-2 hover:underline" onClick={() => setOpenRow(f)} data-testid={`link-failure-${f.id}`}>{f.name}</button><span className="text-muted-foreground break-words">{f.note || "No failure note recorded."}</span></li>
                ))}
              </ul>
            </details>
          )}

          {outcome && (
            <div className="rounded-md border p-3 text-sm space-y-1" data-testid="report-bulk-approval">
              <div className="flex items-start justify-between gap-2">
                <p className="font-medium tabular-nums">Bulk approval: {outcome.approved} approved, {outcome.skipped} skipped{outcome.failedBatches ? `, ${outcome.failedBatches} batch(es) failed` : ""}</p>
                <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => setOutcome(null)} data-testid="button-dismiss-report"><X className="h-3.5 w-3.5" /></Button>
              </div>
              {outcome.errors.map((e) => <p key={e} className="text-xs text-destructive">{e}</p>)}
              {outcome.details.length > 0 && (
                <ul className="text-xs text-muted-foreground max-h-48 overflow-y-auto space-y-0.5">
                  {outcome.details.map((d) => <li key={d.id}><span className="text-foreground">{nameById.get(d.id) ?? d.id}</span>: {d.reason}</li>)}
                </ul>
              )}
            </div>
          )}

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">UK default tasks</CardTitle>
              <CardDescription>{filtered.length} of {rows.length} tasks · ranked top 50 first, then A to Z · prices are AI estimates for owner review</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex flex-wrap gap-2">
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger className="w-full sm:w-52 h-8 text-xs" data-testid="select-filter-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all" className="text-xs">All categories</SelectItem>
                    {categories.map((c) => <SelectItem key={c} value={c} className="text-xs">{c}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={status} onValueChange={(v) => setStatus(v as StatusFilter)}>
                  <SelectTrigger className="w-full sm:w-48 h-8 text-xs" data-testid="select-filter-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all" className="text-xs">All statuses</SelectItem>
                    {(Object.keys(STATUS_LABELS) as RecipeDraftRow["status"][]).map((s) => <SelectItem key={s} value={s} className="text-xs">{STATUS_LABELS[s]}</SelectItem>)}
                    <SelectItem value="ready" className="text-xs">Ready to approve</SelectItem>
                    <SelectItem value="unbalanced" className="text-xs">Unbalanced or invalid</SelectItem>
                    <SelectItem value="stale" className="text-xs">Stale</SelectItem>
                    <SelectItem value="failed" className="text-xs">Drafting failed</SelectItem>
                  </SelectContent>
                </Select>
                <div className="relative flex-1 min-w-[180px]">
                  <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search task name" className="h-8 pl-8 text-sm" data-testid="input-search-drafts" />
                </div>
              </div>

              {selected.size > 0 && (
                <div className="sticky top-0 z-10 flex flex-wrap items-center gap-2 rounded-md border bg-card p-2 text-xs" data-testid="bar-selection">
                  <span className="font-medium tabular-nums" data-testid="text-selected-count">{selected.size} selected</span>
                  {selectedBlocked > 0 && <span className="text-muted-foreground">{selectedBlocked} look ineligible and will be skipped by the server</span>}
                  {batchCount > 1 && <span className="text-muted-foreground">Sent in {batchCount} batches of up to {APPROVAL_BATCH_SIZE}</span>}
                  <div className="ml-auto flex gap-2">
                    <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())} disabled={!!bulkRunning} data-testid="button-clear-selection">Clear</Button>
                    <Button size="sm" onClick={() => setConfirmBulk(true)} disabled={!!bulkRunning} data-testid="button-approve-selected">
                      {bulkRunning ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1" />}
                      {bulkRunning ? `Batch ${bulkRunning.done} of ${bulkRunning.total}` : "Approve selected"}
                    </Button>
                  </div>
                </div>
              )}

              {rows.length === 0 ? (
                <div className="py-10 text-center space-y-1" data-testid="empty-recipe-drafts">
                  <p className="text-sm font-medium">No UK default tasks found</p>
                  <p className="text-xs text-muted-foreground">The master library has no UK defaults to review.</p>
                </div>
              ) : filtered.length === 0 ? (
                <div className="py-10 text-center space-y-2" data-testid="empty-filtered-drafts">
                  <p className="text-sm text-muted-foreground">No tasks match these filters.</p>
                  <Button size="sm" variant="outline" onClick={() => { setCategory("all"); setStatus("all"); setSearch(""); }} data-testid="button-reset-filters">Reset filters</Button>
                </div>
              ) : (
                <div className="overflow-x-auto -mx-2 sm:mx-0">
                  <Table className="text-xs">
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-8"><Checkbox checked={allFilteredSelected} onCheckedChange={(v) => toggleAllFiltered(v === true)} aria-label="Select all shown" data-testid="checkbox-select-all" /></TableHead>
                        <TableHead className="w-12 text-right" title="Development-derived usage rank">Rank*</TableHead>
                        <TableHead>Task</TableHead>
                        <TableHead className="hidden md:table-cell">Category</TableHead>
                        <TableHead>Unit</TableHead>
                        <TableHead className="text-right">Typical</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Difference</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filtered.map((r) => {
                        const ok = isBalanced(r);
                        return (
                          <TableRow key={r.id} className="cursor-pointer" onClick={() => setOpenRow(r)} data-testid={`row-draft-${r.id}`}>
                            <TableCell onClick={(e) => e.stopPropagation()}>
                              <Checkbox checked={selected.has(r.id)} onCheckedChange={(v) => toggle(r.id, v === true)} aria-label={`Select ${r.name}`} data-testid={`checkbox-draft-${r.id}`} />
                            </TableCell>
                            <TableCell className="text-right tabular-nums text-muted-foreground">{r.usageRank ?? "—"}</TableCell>
                            <TableCell className="max-w-[260px]">
                              <p className="font-medium text-sm truncate" title={r.name}>{r.name}</p>
                              <p className="md:hidden text-muted-foreground truncate">{r.category}</p>
                            </TableCell>
                            <TableCell className="hidden md:table-cell text-muted-foreground whitespace-nowrap">{r.category}</TableCell>
                            <TableCell className="whitespace-nowrap">{r.unit}</TableCell>
                            <TableCell className="text-right tabular-nums whitespace-nowrap font-medium">{formatGBP(r.currentTypical)}</TableCell>
                            <TableCell>
                              <div className="flex flex-wrap gap-1">
                                <Badge className={`text-[10px] font-medium no-default-hover-elevate ${STATUS_TONE[r.status]}`} data-testid={`status-draft-${r.id}`}>{STATUS_LABELS[r.status]}</Badge>
                                {!!r.reviewWarnings?.length && <Badge variant="outline" className="text-[10px] text-amber-700" title={r.reviewWarnings.join("; ")}>Scope review</Badge>}
                                {r.generationState === "queued" && <Badge variant="outline" className="text-[10px]">Queued</Badge>}
                                {r.generationState === "running" && <Badge variant="outline" className="text-[10px]">Drafting</Badge>}
                                {r.generationState === "failed" && <Badge variant="outline" className="text-[10px] text-destructive border-destructive/40">Failed</Badge>}
                                {r.isStale && <Badge variant="outline" className="text-[10px] text-amber-700 border-amber-400">Stale</Badge>}
                              </div>
                            </TableCell>
                            <TableCell className={`text-right tabular-nums whitespace-nowrap font-medium ${r.invalid ? "text-destructive" : r.sumDifference === null ? "text-muted-foreground" : ok ? "text-emerald-700 dark:text-emerald-400" : "text-destructive"}`} data-testid={`text-diff-${r.id}`}>
                              {r.invalid ? "Invalid" : formatSignedGBP(r.sumDifference)}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                  <p className="text-[11px] text-muted-foreground mt-2 px-2 sm:px-0">*Rank is development-derived from the Step 1 top-50 list, not production usage. Tasks outside the list show —.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}

      <RecipeDraftReviewDialog row={openRow} onClose={() => setOpenRow(null)} />

      <AlertDialog open={confirmBulk} onOpenChange={setConfirmBulk}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Approve {selectedRows.length} selected?</AlertDialogTitle>
            <AlertDialogDescription>
              Only exactly balanced, valid, current drafts are published; the server skips the rest and reports why.
              {selectedBlocked > 0 && ` ${selectedBlocked} selected look ineligible now.`}
              {batchCount > 1 && ` The selection is sent in ${batchCount} batches of up to ${APPROVAL_BATCH_SIZE}; each batch succeeds or rolls back on its own.`}
              {" "}Typical, low, high and lock stay unchanged.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-bulk">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); void runBulkApprove(); }} data-testid="button-confirm-bulk">Approve</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmBaseline} onOpenChange={(o) => { if (!baseline.isPending) setConfirmBaseline(o); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Refresh the safety snapshot?</AlertDialogTitle>
            <AlertDialogDescription>
              This re-captures every UK default's typical, low, high and lock, and all saved quote totals, as they are now. It does NOT fix or revert any changed price. Drafts made against an old price stay stale and still need redrafting before they can be approved.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={baseline.isPending} data-testid="button-cancel-baseline">Cancel</AlertDialogCancel>
            <AlertDialogAction disabled={baseline.isPending} onClick={(e) => { e.preventDefault(); baseline.mutate({ data: { confirm: true } }); }} data-testid="button-confirm-baseline">
              {baseline.isPending ? "Refreshing..." : "Refresh snapshot"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
